import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject } from 'rxjs';

export interface ChatSession {
	id: string;
	title: string;
	createdAt: number;
	lastActivity: number;
	messageCount: number;
	requestCount?: number;  // Total requests made (including filtered ones)
	locale?: string;
}

export interface ChatMessage {
	role: 'user' | 'assistant';
	content: string;
	timestamp: number;
}

@Injectable()
export class AiHelperService {

	sessions$ = new BehaviorSubject<ChatSession[]>([]);
	messages$ = new BehaviorSubject<ChatMessage[]>([]);
	loading$ = new BehaviorSubject<boolean>(false);
	activeSessionId$ = new BehaviorSubject<string | null>(null);
	/** Locale code for the currently active session (e.g. 'en', 'fr-CA'). */
	activeLocale$ = new BehaviorSubject<string>('en');

	constructor(private http: HttpClient) { }

	loadSessions() {
		// Step 1: Load from Redis immediately to populate the list quickly
		this.http.get<{ sessions: ChatSession[] }>('/ai-helper/sessions').subscribe(
			res => {
				this.sessions$.next(res.sessions || []);
				// Step 2: Sync with Langfuse in the background for any prior sessions
				// that may have been evicted from Redis
				this.syncWithLangfuse();
			},
			err => console.error('Failed to load sessions', err)
		);
	}

	private syncWithLangfuse() {
		this.http.get<{ sessions: ChatSession[]; added: number }>('/ai-helper/sessions/sync').subscribe(
			res => {
				if (res.added > 0) {
					// Merge rather than replace — preserves any local deletions made while the sync was in-flight.
					// The server snapshot was built before those deletes, so a full replace would restore them.
					const currentIds = new Set(this.sessions$.value.map(s => s.id));
					const genuinelyNew = (res.sessions || []).filter(s => !currentIds.has(s.id));
					if (genuinelyNew.length > 0) {
						console.log(`[ai-helper] Langfuse sync added ${genuinelyNew.length} prior session(s)`);
						this.sessions$.next([...genuinelyNew, ...this.sessions$.value]);
					}
				}
			},
			err => console.error('[ai-helper] Langfuse sync failed (non-critical)', err)
		);
	}

	createSession(title?: string) {
		this.loading$.next(true);
		const locale = this.activeLocale$.value !== 'en' ? this.activeLocale$.value : undefined;
		const body: any = { title: title || 'New Chat' };
		if (locale) body.locale = locale;

		this.http.post<ChatSession>('/ai-helper/sessions', body).subscribe(
			session => {
				const current = this.sessions$.value;
				this.sessions$.next([session, ...current]);
				this.activeSessionId$.next(session.id);
				this.messages$.next([]);
				this.loading$.next(false);
			},
			err => {
				console.error('Failed to create session', err);
				this.loading$.next(false);
			}
		);
	}

	loadMessages(sessionId: string) {
		this.activeSessionId$.next(sessionId);
		this.loading$.next(true);
		this.http.get<{ session: ChatSession; messages: ChatMessage[] }>(`/ai-helper/sessions/${sessionId}`).subscribe(
			res => {
				// Ensure all message content is a string (fix [object Object] from old data).
				// Also strip any tool_hints metadata that may have survived the backend
				// sanitizer (e.g. from a Langfuse-restored session). Users must only see
				// the text they originally typed.
				const messages = (res.messages || []).map(msg => {
					let content = typeof msg.content === 'string'
						? msg.content
						: (msg.content as any)?.message || JSON.stringify(msg.content);
					// Belt-and-suspenders: remove any inline tool_hints fragment
					content = content.replace(/"?tool_hints"?\s*:\s*\[[^\]]*\]/gi, '').trim();
					return { ...msg, content };
				});
				console.log('[ai-helper] loaded messages:', messages.length, messages);
				this.messages$.next(messages);

				// Restore locale preference from session metadata
				if (res.session?.locale) {
					this.activeLocale$.next(res.session.locale);
				}

				// Update this session's messageCount in the sidebar to reflect actual count
				const actualCount = messages.length;
				const sessions = this.sessions$.value.map(s =>
					s.id === sessionId ? { ...s, messageCount: actualCount, locale: res.session?.locale } : s
				);
				this.sessions$.next(sessions);

				this.loading$.next(false);
			},
			err => {
				console.error('[ai-helper] Failed to load messages', err);
				this.loading$.next(false);
			}
		);
	}

	sendMessage(sessionId: string, message: string) {
		this.loading$.next(true);

		// Optimistically add user message
		const userMsg: ChatMessage = { role: 'user', content: message, timestamp: Date.now() };
		this.messages$.next([...this.messages$.value, userMsg]);

		const payload = { sessionId, message };
		console.log('[ai-helper] sending chat request:', payload);

		this.http.post<{ response: string; sessionId: string }>('/ai-helper/chat', payload).subscribe(
			res => {
				console.log('[ai-helper] chat response:', res);
				const responseText = typeof res.response === 'string' ? res.response : JSON.stringify(res.response);
				const botMsg: ChatMessage = { role: 'assistant', content: responseText, timestamp: Date.now() };
				this.messages$.next([...this.messages$.value, botMsg]);

				// Update session message count in local list
				const sessions = this.sessions$.value.map(s => {
					if (s.id === sessionId) {
						return { ...s, messageCount: (s.messageCount || 0) + 2, lastActivity: Date.now() };
					}
					return s;
				});
				this.sessions$.next(sessions);
				this.loading$.next(false);
			},
			err => {
				console.error('[ai-helper] chat error:', err.status, err.statusText, err.error);
				const serverMsg = err.error?.error || 'Sorry, I was unable to process your request. Please try again.';
				const errorMsg: ChatMessage = {
					role: 'assistant',
					content: serverMsg,
					timestamp: Date.now(),
				};
				this.messages$.next([...this.messages$.value, errorMsg]);
				this.loading$.next(false);
			}
		);
	}

	/**
	 * Persist a locale preference for the active session.
	 * Updates Redis session metadata so all subsequent messages use the correct
	 * i18n normalization and formatting pipeline.
	 */
	setSessionLocale(sessionId: string, locale: string) {
		this.activeLocale$.next(locale);

		// Update local session list
		const sessions = this.sessions$.value.map(s =>
			s.id === sessionId ? { ...s, locale } : s
		);
		this.sessions$.next(sessions);

		this.http.patch<{ success: boolean; locale: string }>(
			`/ai-helper/sessions/${sessionId}/locale`,
			{ locale }
		).subscribe(
			() => console.log(`[ai-helper] locale set to ${locale} for session ${sessionId}`),
			err => console.error('[ai-helper] Failed to set locale', err)
		);
	}

	deleteSession(sessionId: string) {
		this.http.delete<{ success: boolean }>(`/ai-helper/sessions/${sessionId}`).subscribe(
			() => {
				this.sessions$.next(this.sessions$.value.filter(s => s.id !== sessionId));
				if (this.activeSessionId$.value === sessionId) {
					this.activeSessionId$.next(null);
					this.activeLocale$.next('en');
					this.messages$.next([]);
				}
			},
			err => console.error('Failed to delete session', err)
		);
	}
}
